import type { TurmaDto } from '@sheepcount/shared';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Select } from '@/components/ui/select';

const STORAGE_KEY = 'sheepcount.turmaFiltro';

interface TurmaFilterContextValue {
  /** '' = todas as turmas. */
  turmaId: string;
  setTurmaId: (id: string) => void;
}

const TurmaFilterContext = createContext<TurmaFilterContextValue | null>(null);

/**
 * Filtro global "ver só uma turma", compartilhado entre as telas de Alunos,
 * Presença e Painel e persistido entre sessões.
 */
export function TurmaFilterProvider({ children }: { children: ReactNode }) {
  const [turmaId, setTurmaIdState] = useState<string>(
    () => localStorage.getItem(STORAGE_KEY) ?? '',
  );

  const setTurmaId = useCallback((id: string) => {
    setTurmaIdState(id);
    if (id === '') {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, id);
    }
  }, []);

  const value = useMemo(() => ({ turmaId, setTurmaId }), [turmaId, setTurmaId]);
  return <TurmaFilterContext.Provider value={value}>{children}</TurmaFilterContext.Provider>;
}

export function useTurmaFilter(): TurmaFilterContextValue {
  const ctx = useContext(TurmaFilterContext);
  if (!ctx) throw new Error('useTurmaFilter deve ser usado dentro de <TurmaFilterProvider>');
  return ctx;
}

/**
 * Filtro persistido validado contra a lista de turmas visíveis: se a turma
 * salva não está mais acessível (ex.: professor desvinculado), cai em "todas".
 */
export function turmaFiltroValido(turmaId: string, turmas: TurmaDto[]): string {
  return turmas.some((t) => t.id === turmaId) ? turmaId : '';
}

export function TurmaFilterSelect({
  turmas,
  className,
}: {
  turmas: TurmaDto[];
  className?: string;
}) {
  const { turmaId, setTurmaId } = useTurmaFilter();
  return (
    <Select
      aria-label="Filtrar por turma"
      className={className ?? 'w-auto min-w-[170px]'}
      value={turmaFiltroValido(turmaId, turmas)}
      onChange={(e) => setTurmaId(e.target.value)}
    >
      <option value="">Todas as turmas</option>
      {turmas.map((turma) => (
        <option key={turma.id} value={turma.id}>
          {turma.nome}
        </option>
      ))}
    </Select>
  );
}
